// Inline bar (Chart UX pass 2026-10-21; Mosaic / Runway / Brex budgets): a thin track with a fill
// at `value` per cent of its width, for a table cell, a card footer or a KPI tile's detail line.
// Not a chart: no axis, no hover layer; the number stands beside it in text. `tone` colours the
// fill for a budget read (within / near / over) or a plain measure.
import type * as React from "react";

import { cn } from "../cn";

export type InlineBarTone = "primary" | "success" | "warning" | "danger" | "neutral";

const FILL: Record<InlineBarTone, string> = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  neutral: "bg-muted-foreground",
};

export type InlineBarProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** 0–100; anything beyond is clamped. */
  value: number;
  tone?: InlineBarTone;
  /** Track height: 4 px (`xs`) or 6 px (`sm`). */
  size?: "xs" | "sm";
};

export function InlineBar({
  value,
  tone = "primary",
  size = "sm",
  className,
  ...props
}: InlineBarProps) {
  const pct = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  return (
    <span
      data-slot="inline-bar"
      data-tone={tone}
      className={cn(
        "block w-full overflow-hidden rounded-full bg-muted",
        size === "xs" ? "h-1" : "h-1.5",
        className,
      )}
      {...props}
    >
      <span className={cn("block h-full rounded-full", FILL[tone])} style={{ width: `${pct}%` }} />
    </span>
  );
}
